import React, { useState } from 'react';
import type { University, UniversityFormData } from '../../types';
import Modal from '../ui/Modal';

interface UniversityFormProps {
  university?: University | null;
  onSave: (data: UniversityFormData) => Promise<void>;
  onClose: () => void;
}

const UniversityForm: React.FC<UniversityFormProps> = ({ university, onSave, onClose }) => {
  const [formData, setFormData] = useState<UniversityFormData>({
    name: university?.name || '',
    code: university?.code || '',
    domain: university?.domain || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setError('');

    if (!formData.name || !formData.code || !formData.domain) {
      setError('Please fill in all fields.');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save university.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={university ? 'Edit University' : 'Add New University'} onClose={onClose}>
      <div className="p-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">University Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter university name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">University Code</label>
          <input
            type="text"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="e.g., MIT, STAN"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Domain</label>
          <input
            type="text"
            value={formData.domain}
            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="e.g., university.edu"
          />
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : university ? 'Update' : 'Add'} University
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UniversityForm;
